import { devDebug } from "./devLog";
// DM sound preference — persisted opt-in ping when a new encrypted DM
// arrives in a background tab. Uses Web Audio API to synthesize a short
// two-note chime at runtime, so no audio asset needs to be shipped or
// served. Preference lives in the zero-knowledge localStore wrapper.
//
// Contract:
//   • `isDmSoundEnabled()` returns true by default (opt-out).
//   • `setDmSoundEnabled(bool)` persists the toggle.
//   • `playDmPing()` no-ops when muted, when the page is visible (user is
//     already looking at the app), or when the browser blocks autoplay.

import { readItem, writeItem } from "./localStore";

const KEY = "ol_notification_sound_v1";
const LEGACY_KEY = "ol_dm_sound_v1";
export const NOTIFICATION_SOUND_EVENT = "ol:notification-sound-change";

export function isDmSoundEnabled() {
  const raw = readItem(KEY);
  const legacy = readItem(LEGACY_KEY);
  // Default: enabled. Preserve an existing DM mute preference during migration.
  return raw == null ? legacy !== "0" : raw !== "0";
}

export function setDmSoundEnabled(enabled) {
  writeItem(KEY, enabled ? "1" : "0");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_SOUND_EVENT, { detail: { enabled } }));
  }
}

let sharedCtx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx) {
    try { sharedCtx = new AC(); } catch (err) {
      devDebug("dmSoundPref: AudioContext unavailable", err?.message);
      return null;
    }
  }
  return sharedCtx;
}

/**
 * Plays a short two-note "ping" chime. Best-effort — silently skips when:
 *   • user has muted the setting;
 *   • autoplay policies reject the play (returns immediately).
 */
export function playDmPing() {
  if (!isDmSoundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  try {
    // Resume in case the context was auto-suspended by autoplay policy.
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    master.connect(ctx.destination);
    // Two overlapping sine notes with soft attack/release for a discreet chime.
    const notes = [
      { freq: 880, start: 0, dur: 0.18 },     // A5
      { freq: 1318.5, start: 0.09, dur: 0.22 }, // E6
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = n.freq;
      const t0 = now + n.start;
      const t1 = t0 + n.dur;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t1);
      osc.connect(g).connect(master);
      osc.start(t0);
      osc.stop(t1 + 0.02);
    }
    // Bring master gain up to 1 for the duration of the chime.
    master.gain.setValueAtTime(1, now);
    master.gain.setValueAtTime(1, now + 0.35);
    master.gain.linearRampToValueAtTime(0.0001, now + 0.4);
  } catch (err) {
    devDebug("playDmPing failed", err?.message);
  }
}
